import { useState } from 'react'
import { UserCircleIcon } from '@heroicons/react/24/outline'
import { supabase } from '../services/supabaseClient'
import { PageLayout } from './PageLayout'

type Props = {
  userId: string | null
  email: string | null
  avatarUrl: string | null
  displayName: string | null
  onAvatarChange: (url: string | null, path: string | null) => void
  onDisplayNameChange: (name: string | null) => void
}

export const ProfilePage = ({ userId, email, avatarUrl, displayName, onAvatarChange, onDisplayNameChange }: Props) => {
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [localDisplayName, setLocalDisplayName] = useState(displayName ?? '')
  const [savingName, setSavingName] = useState(false)

  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}
  const AVATAR_BUCKET = env.VITE_SUPABASE_AVATAR_BUCKET ?? 'avatars'

  const handleNameSave = async () => {
    setSavingName(true)
    setError(null)
    setMessage(null)
    try {
      const trimmedName = localDisplayName.trim() || null
      onDisplayNameChange(trimmedName)
      setMessage('Display name saved.')
    } catch {
      setError('Failed to save display name.')
    } finally {
      setSavingName(false)
    }
  }

  const handleFileChange = async (file: File | null) => {
    if (!file || !userId) {
      setError('Select a file and make sure you are signed in.')
      return
    }
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed.')
      return
    }
    const MAX_SIZE = 2 * 1024 * 1024 // 2MB
    if (file.size > MAX_SIZE) {
      setError('File is too large (2MB limit).')
      return
    }
    setUploading(true)
    setMessage(null)
    setError(null)

    const ext = file.name.split('.').pop() ?? 'jpg'
    const path = `${userId}/avatar-${Date.now()}.${ext}`

    const { error: uploadError } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, {
      upsert: true,
    })
    if (uploadError) {
      setError(uploadError.message ?? 'Failed to upload the file.')
      setUploading(false)
      return
    }

    const { data } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path)
    const publicUrl = data?.publicUrl ?? null

    const { error: updateError } = await supabase.auth.updateUser({
      data: { avatar_path: path },
    })
    if (updateError) {
      setError(updateError.message ?? 'Failed to save avatar.')
      setUploading(false)
      return
    }

    setMessage('Avatar saved.')
    onAvatarChange(publicUrl, path)
    setUploading(false)
  }

  return (
    <PageLayout
      icon={UserCircleIcon}
      title="Profile"
      subtitle="Manage your account and avatar"
    >
      <div className="card p-6 space-y-6">
        {/* Avatar section */}
        <div className="flex flex-wrap items-center gap-6">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border border-app-line bg-app-page">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="h-full w-full object-cover" />
              ) : (
                <svg className="h-12 w-12 text-app-muted" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              )}
            </div>
            <div className="space-y-3">
              <p className="text-sm text-app-muted">
                Email: <span className="font-semibold text-app-ink">{email ?? '—'}</span>
              </p>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-app-line bg-app-subtle px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-line transition-colors">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                </svg>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                  disabled={uploading}
                />
                {uploading ? 'Uploading...' : 'Change avatar'}
              </label>
            </div>
          </div>

        {/* Display name */}
        <div className="border border-app-line bg-app-page/60 p-4 space-y-3 rounded-2xl shadow-app-card">
          <div>
            <p className="font-semibold text-app-ink">Display name</p>
            <p className="text-sm text-app-muted">
                This name will be shown instead of your email address throughout the app.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              value={localDisplayName}
              onChange={(e) => setLocalDisplayName(e.target.value)}
              placeholder="Enter your name..."
              className="flex-1 min-w-[200px] rounded-lg border border-app-line bg-app-page px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none"
              disabled={savingName}
            />
            <button
              type="button"
              onClick={handleNameSave}
              disabled={savingName || localDisplayName === (displayName ?? '')}
              className="rounded-lg bg-app-primary px-4 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover disabled:opacity-60 transition-colors"
            >
              {savingName ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {message && <p className="text-sm text-emerald-700 dark:text-emerald-400">{message}</p>}
        {error && <p className="text-sm text-rose-700 dark:text-rose-400">{error}</p>}
      </div>
    </PageLayout>
  )
}
